import { BrowserProvider, parseUnits } from 'ethers';

type HexChainId = `0x${string}`;

export interface WalletPaymentParams {
  walletProvider: any; // EIP-1193 provider from AppKit
  recipientAddress: string;
  chainId: number;
  /**
   * Amount expressed in base units (wei or token units). Preferred over amountHuman.
   */
  amountBaseUnits?: string;
  /**
   * Human readable amount (e.g. "0.01"). Used when base units are not supplied.
   */
  amountHuman?: string;
  /**
   * Token contract address for ERC-20 transfers. Undefined for native asset payments.
   */
  tokenAddress?: string;
  /**
   * Number of decimals to use when converting human-readable amounts.
   */
  decimals?: number;
}

export interface WalletPaymentResult {
  txHash: string;
  success: true;
}

export class WalletPaymentError extends Error {
  code?: number;
  
  constructor(message: string, code?: number) {
    super(message);
    this.name = 'WalletPaymentError';
    this.code = code;
  }
}

/**
 * Simple, clean wallet payment using AppKit + ethers
 * Based on Grok's recommended approach
 */
export async function sendWalletPayment(params: WalletPaymentParams): Promise<WalletPaymentResult> {
  const {
    walletProvider,
    recipientAddress,
    chainId,
    amountBaseUnits,
    amountHuman,
    tokenAddress,
    decimals
  } = params;

  console.log('🔍 sendWalletPayment called with:', {
    hasWalletProvider: !!walletProvider,
    walletProviderType: typeof walletProvider,
    recipientAddress,
    chainId,
    amountBaseUnits,
    amountHuman,
    tokenAddress,
    decimals
  });

  if (!walletProvider) {
    throw new WalletPaymentError('Wallet provider not available');
  }

  if (typeof chainId !== 'number' || Number.isNaN(chainId)) {
    throw new WalletPaymentError('A valid chainId is required for wallet payments');
  }

  if (!recipientAddress || recipientAddress.length !== 42 || !recipientAddress.startsWith('0x')) {
    throw new WalletPaymentError(`Invalid recipient address: ${recipientAddress}`);
  }

  const resolvedAmount = (() => {
    if (amountBaseUnits) {
      try {
        return BigInt(amountBaseUnits);
      } catch (error) {
        throw new WalletPaymentError(`Invalid base unit amount: ${amountBaseUnits}`);
      }
    }

    if (!amountHuman) {
      throw new WalletPaymentError('Payment amount is missing');
    }

    const unitDecimals = typeof decimals === 'number' ? decimals : tokenAddress ? 18 : 18;
    try {
      return parseUnits(amountHuman, unitDecimals);
    } catch (error) {
      throw new WalletPaymentError(`Unable to parse payment amount: ${amountHuman}`);
    }
  })();

  const normalizeChainId = (value: number): HexChainId => `0x${value.toString(16)}` as HexChainId;

  const encodeErc20Transfer = (to: string, value: bigint): `0x${string}` => {
    const functionSelector = '0xa9059cbb';
    const paddedAddress = to.replace(/^0x/, '').padStart(64, '0');
    const paddedValue = value.toString(16).padStart(64, '0');
    return `${functionSelector}${paddedAddress}${paddedValue}` as `0x${string}`;
  };

  try {
    console.log('🔄 Creating BrowserProvider...');
    // Create provider and signer using ethers v6
    const provider = new BrowserProvider(walletProvider, chainId);
    
    console.log('🔄 Getting signer...');
    const signer = await provider.getSigner();
    const signerAddress = await signer.getAddress();
    const providerNetwork = await provider.getNetwork();

    console.log('✅ Signer obtained:', {
      address: signerAddress,
      chainId: providerNetwork.chainId
    });

    // Prepare pre-filled transaction
    const chainIdHex = normalizeChainId(chainId);

    const tx = tokenAddress
      ? {
          to: tokenAddress,
          data: encodeErc20Transfer(recipientAddress, resolvedAmount),
          value: 0n,
          chainId,
          // Some wallets expect from to be populated explicitly when deep linked
          from: signerAddress
        }
      : {
          to: recipientAddress,
          value: resolvedAmount,
          chainId,
          from: signerAddress
        };

    console.log('🔄 Sending transaction:', {
      to: tx.to,
      value: tokenAddress ? resolvedAmount.toString() : (tx as { value: bigint }).value.toString(),
      chainId,
      chainIdHex,
      tokenTransfer: !!tokenAddress
    });

    // Send transaction - wallet will prompt user to confirm
    const txResponse = await signer.sendTransaction(tx);
    
    console.log('✅ Transaction sent:', txResponse.hash);

    return {
      txHash: txResponse.hash,
      success: true
    };

  } catch (error: any) {
    console.error('❌ Wallet payment failed:', error);

    // Handle user rejection
    if (error.code === 4001) {
      throw new WalletPaymentError('Transaction rejected by user', 4001);
    }

    // Handle insufficient funds
    if (error.code === -32000 || error.message?.includes('insufficient funds')) {
      throw new WalletPaymentError('Insufficient funds for transaction', -32000);
    }

    // Handle network errors
    if (error.code === -32603) {
      throw new WalletPaymentError('Network error during transaction', -32603);
    }

    // Generic error
    throw new WalletPaymentError(
      error.message || 'Transaction failed',
      error.code
    );
  }
}
